import { sseClient } from '@app/api/sseService';
import { useAppDispatch } from '@app/store/hooks';
import { useEffect, useRef } from 'react';

/**
 * Hook to connect SSE and auto-invalidate RTK Query cache on server events.
 *
 * Call this ONCE in a top-level authenticated component (e.g., Layout).
 * The SSE connection is managed as a singleton, so multiple calls are safe.
 */
export function useSSE(): void {
    const dispatch = useAppDispatch();
    const connectedRef = useRef(false);

    useEffect(() => {
        if (!connectedRef.current) {
            sseClient.connect(dispatch);
            connectedRef.current = true;
        }

        return () => {
            sseClient.disconnect();
            connectedRef.current = false;
        };
    }, [dispatch]);
}

/**
 * Hook to listen for a specific SSE event type.
 *
 * @param eventType  The SSE event name (e.g., 'pallet:received') or '*' for all
 * @param callback   Called with (eventType, data) when event arrives
 *
 * @example
 * useSSEEvent('pallet:received', (event, data) => {
 *   console.log('New pallet:', data);
 * });
 */
export function useSSEEvent(
    eventType: string,
    callback: (event: string, data: Record<string, unknown>) => void
): void {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const handler = (event: string, data: Record<string, unknown>) => {
            callbackRef.current(event, data);
        };

        sseClient.on(eventType, handler);

        return () => {
            sseClient.off(eventType, handler);
        };
    }, [eventType]);
}
